import React, { useEffect, useState } from 'react';
import { Users, Loader2, ArrowDown, TrendingUp, Target, Phone, MessageSquare, UserCheck } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 7, label: 'Last 7 Days' },
  { value: 30, label: 'Last 30 Days' },
  { value: 90, label: 'Last 90 Days' },
  { value: null, label: 'All Time' },
];

const FUNNEL_STEPS = [
  { key: 'identified', label: 'Identified', color: 'bg-blue-500', lightBg: 'bg-blue-50', text: 'text-blue-700', icon: Users },
  { key: 'contacted', label: 'Contacted', color: 'bg-indigo-500', lightBg: 'bg-indigo-50', text: 'text-indigo-700', icon: Phone },
  { key: 'responses', label: 'Responses', color: 'bg-amber-500', lightBg: 'bg-amber-50', text: 'text-amber-700', icon: MessageSquare },
  { key: 'conversations', label: 'Conversations', color: 'bg-purple-500', lightBg: 'bg-purple-50', text: 'text-purple-700', icon: TrendingUp },
  { key: 'opportunities', label: 'Opportunities', color: 'bg-orange-500', lightBg: 'bg-orange-50', text: 'text-orange-700', icon: Target },
  { key: 'clients', label: 'Clients', color: 'bg-green-500', lightBg: 'bg-green-50', text: 'text-green-700', icon: UserCheck },
];

const ClientAcquisitionTracker = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    setLoading(true);
    const url = period ? `${API_URL}/api/intelligence/client-acquisition?days=${period}` : `${API_URL}/api/intelligence/client-acquisition`;
    fetchWithAuth(url).then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  const funnel = data?.funnel || {};
  const rates = data?.conversion_rates || {};
  const maxVal = Math.max(...Object.values(funnel).filter(v => typeof v === 'number'), 1);

  return (
    <IntelligenceLayout title="Client Acquisition" embedded={embedded}>
      <div className="p-6 space-y-6" data-testid="client-acquisition-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Client Acquisition Performance</h1>
            <p className="text-sm text-gray-500 mt-1">Track how recruiting outreach converts into clients</p>
          </div>
          <div className="flex gap-2" data-testid="period-filters">
            {PERIODS.map(p => (
              <button key={p.label} onClick={() => setPeriod(p.value)} data-testid={`period-${p.value || 'all'}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all ${
                  period === p.value ? 'bg-[#0A1F44] text-white border-[#0A1F44]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#D4AF37]'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : (
          <>
            {/* Conversion Rate Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Contact Rate" value={`${rates.contact_rate}%`} subtext="Contacted / Identified" icon={Phone} />
              <MetricCard label="Response Rate" value={`${rates.response_rate}%`} subtext="Responses / Contacted" icon={MessageSquare} />
              <MetricCard label="Conversation Rate" value={`${rates.conversation_rate}%`} subtext="Conversations / Responses" icon={TrendingUp} />
              <MetricCard label="Client Conversion" value={`${rates.client_conversion_rate}%`} subtext="Clients / Contacted" icon={UserCheck} />
            </div>

            {/* Funnel Visualization */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-testid="funnel-chart">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-6">Acquisition Funnel</h3>
              <div className="space-y-3 max-w-2xl mx-auto">
                {FUNNEL_STEPS.map((step, i) => {
                  const value = funnel[step.key] || 0;
                  const widthPct = maxVal > 0 ? Math.max(8, (value / maxVal) * 100) : 8;
                  const prevValue = i > 0 ? (funnel[FUNNEL_STEPS[i - 1].key] || 0) : null;
                  const convRate = prevValue && prevValue > 0 ? Math.round((value / prevValue) * 100) : null;
                  return (
                    <div key={step.key}>
                      {i > 0 && (
                        <div className="flex items-center justify-center py-1">
                          <ArrowDown className="h-4 w-4 text-gray-300" />
                          {convRate !== null && <span className="ml-2 text-[10px] font-semibold text-gray-400">{convRate}% conversion</span>}
                        </div>
                      )}
                      <div className="flex items-center gap-3" data-testid={`funnel-${step.key}`}>
                        <div className={`w-8 h-8 rounded-lg ${step.lightBg} flex items-center justify-center`}>
                          <step.icon className={`h-4 w-4 ${step.text}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-[#0A1F44]">{step.label}</span>
                            <span className="text-sm font-bold text-[#0A1F44]">{value.toLocaleString()}</span>
                          </div>
                          <div className="h-6 bg-gray-100 rounded-lg overflow-hidden">
                            <div className={`h-full ${step.color} rounded-lg transition-all duration-700`} style={{ width: `${widthPct}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {FUNNEL_STEPS.map(step => (
                <div key={step.key} className={`${step.lightBg} rounded-xl border p-4 text-center`} data-testid={`stat-${step.key}`}>
                  <p className={`text-2xl font-bold ${step.text}`}>{(funnel[step.key] || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">{step.label}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </IntelligenceLayout>
  );
};

export default ClientAcquisitionTracker;
